"use client"

import { useState } from "react"
import {
    Folder as FolderIcon,
    MoreHorizontal,
    Pencil,
    Trash2,
    Link2,
    Share2,
    FolderPlus,
} from "lucide-react"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import type { Folder } from "../../../lib/types"
import { deleteFolder, saveFolder } from "../../../API"

interface FolderCardProps {
    folder: Folder
    projectCount: number
    subfolderCount: number
    onOpen: (folderId: string) => void
    onRename: (folderId: string, name: string) => void
    onDelete: (folderId: string) => void
    onAssignProjects: (folderId: string) => void
    onCreateSubfolder: (parentId: string, name: string, projectIds: number[], id: number) => void
    onShare: (folderId: string) => void
}

export function FolderCard({
    folder,
    projectCount,
    subfolderCount,
    onOpen,
    onRename,
    onDelete,
    onAssignProjects,
    onCreateSubfolder,
    onShare,
}: FolderCardProps) {
    const [isRenaming, setIsRenaming] = useState(false)
    const [newName, setNewName] = useState(folder.name)
    const [isDeleteConfirm, setIsDeleteConfirm] = useState(false)
    const [isCreatingSub, setIsCreatingSub] = useState(false)
    const [subName, setSubName] = useState("")

    const handleRename = () => {
        if (newName.trim()) {
            onRename(folder.id, newName.trim())
            setIsRenaming(false)
        }
    }

    const handleCreateSub = async () => {
        if (subName.trim()) {

            const data = await saveFolder(subName.trim(), folder.id)

            onCreateSubfolder(folder.id, subName.trim(), [], data.id)
            setSubName("")
            setIsCreatingSub(false)
        }
    }

    const handleDelete = (id: string) => {
        onDelete(id)
        deleteFolder(id)
    }

    return (
        <>
            <div
                className="group relative cursor-pointer rounded-lg border border-border bg-card transition-all hover:border-primary/40 hover:bg-accent"
                onClick={() => onOpen(folder.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && onOpen(folder.id)}
                aria-label={`Abrir pasta ${folder.name}`}
            >
                {/* Thumbnail area */}
                <div className="relative flex h-40 items-center justify-center rounded-t-lg bg-secondary/50">
                    <FolderIcon
                        className="size-16 transition-transform group-hover:scale-105"
                        style={{ color: folder.color }}
                        fill={folder.color}
                        fillOpacity={0.2}
                    />
                    {/* Badges */}
                    <div className="absolute right-2 bottom-2 flex items-center gap-1.5">
                        {subfolderCount > 0 && (
                            <div
                                className="flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-foreground"
                                style={{ backgroundColor: folder.color + "22" }}
                            >
                                <FolderIcon className="size-3" style={{ color: folder.color }} />
                                {subfolderCount}
                            </div>
                        )}
                        <div
                            className="flex size-7 items-center justify-center rounded-full text-xs font-bold text-foreground"
                            style={{ backgroundColor: folder.color + "33" }}
                        >
                            {projectCount}
                        </div>
                    </div>
                </div>

                {/* Info */}
                <div className="flex items-center justify-between px-3 py-3">
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">
                            {folder.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {projectCount} {projectCount === 1 ? "projeto" : "projetos"}
                            {subfolderCount > 0 && (
                                <span>
                                    {" / "}
                                    {subfolderCount} {subfolderCount === 1 ? "subpasta" : "subpastas"}
                                </span>
                            )}
                        </p>
                    </div>

                    {/* Actions menu */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="size-8 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
                                onClick={(e) => e.stopPropagation()}
                                aria-label="Menu de opcoes"
                            >
                                <MoreHorizontal className="size-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuItem
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setNewName(folder.name)
                                    setIsRenaming(true)
                                }}
                            >
                                <Pencil className="mr-2 size-4" />
                                Renomear
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setSubName("")
                                    setIsCreatingSub(true)
                                }}
                            >
                                <FolderPlus className="mr-2 size-4" />
                                Criar Subpasta
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onAssignProjects(folder.id)
                                }}
                            >
                                <Link2 className="mr-2 size-4" />
                                Vincular Projetos
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onShare(folder.id)
                                }}
                            >
                                <Share2 className="mr-2 size-4" />
                                Compartilhar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setIsDeleteConfirm(true)
                                }}
                            >
                                <Trash2 className="mr-2 size-4" />
                                Excluir
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Rename Dialog */}
            <Dialog open={isRenaming} onOpenChange={setIsRenaming}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Renomear Pasta</DialogTitle>
                        <DialogDescription>
                            Digite o novo nome para a pasta.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleRename()}
                        autoFocus
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsRenaming(false)}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleRename}
                            disabled={!newName.trim()}
                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                            Salvar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Subfolder Dialog */}
            <Dialog open={isCreatingSub} onOpenChange={setIsCreatingSub}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Criar Subpasta em &quot;{folder.name}&quot;</DialogTitle>
                        <DialogDescription>
                            Escolha um nome para a nova subpasta.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        placeholder="Nome da subpasta"
                        value={subName}
                        onChange={(e) => setSubName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleCreateSub()}
                        autoFocus
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsCreatingSub(false)}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleCreateSub}
                            disabled={!subName.trim()}
                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                            Criar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteConfirm} onOpenChange={setIsDeleteConfirm}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Excluir Pasta</DialogTitle>
                        <DialogDescription>
                            Tem certeza que deseja excluir a pasta &quot;{folder.name}&quot;?
                            {subfolderCount > 0
                                ? " Todas as subpastas tambem serao excluidas."
                                : ""}{" "}
                            Os projetos vinculados nao serao excluidos.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteConfirm(false)}>
                            Cancelar
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                handleDelete(folder.id)
                                setIsDeleteConfirm(false)
                            }}
                        >
                            Excluir
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}